import {
  type ComponentType,
  useEffect,
  createElement,
  type ComponentProps,
} from 'react';
import { useTheme } from '@mui/material/styles';
import type { RemoteConfig, WebpackContainer } from '../types';
import { extractComponent } from '../utils';
import type { RuntimeShim } from './types';
import { loadChunkMap, loadScript } from './script-loader';
import { installChunkInterceptor } from './chunk-interceptor';
import { createRuntimeShim } from './runtime-shim';
import { shareHostReact } from './host-react';
import { registerNavigationShim } from './navigation-shims';
import {
  ensureGlobals,
  ensureCssMarker,
  watchImageSources,
  unwatchImageSources,
} from './polyfills';

const remoteCache = new Map<
  string,
  { container: WebpackContainer; shim: RuntimeShim }
>();

let containerLoadQueue: Promise<void> = Promise.resolve();

const runContainerLoad = async (
  name: string,
  entryUrl: string,
): Promise<{ container: WebpackContainer; shim: RuntimeShim }> => {
  const chunkBaseUrl = entryUrl.substring(0, entryUrl.lastIndexOf('/') + 1);

  const chunkMap = await loadChunkMap(chunkBaseUrl);
  const shim = createRuntimeShim(chunkBaseUrl, chunkMap);

  shareHostReact(shim);
  ensureGlobals();
  ensureCssMarker();

  installChunkInterceptor(shim);

  const timestampedEntryUrl = new URL(entryUrl);
  timestampedEntryUrl.searchParams.set('t', String(Date.now()));
  const entryUrlWithTimestamp = timestampedEntryUrl.toString();

  const container = await loadScript<WebpackContainer>(
    name,
    entryUrlWithTimestamp,
  );

  const result = { container, shim };
  remoteCache.set(name, result);
  return result;
};

const loadRemoteContainer = async (
  name: string,
  entryUrl: string,
): Promise<{ container: WebpackContainer; shim: RuntimeShim }> => {
  const cached = remoteCache.get(name);
  if (cached) {
    return cached;
  }

  return new Promise<{ container: WebpackContainer; shim: RuntimeShim }>(
    (resolve, reject) => {
      containerLoadQueue = containerLoadQueue
        .catch(() => undefined)
        .then(async () => {
          const hit = remoteCache.get(name);
          if (hit) {
            resolve(hit);
            return;
          }
          try {
            resolve(await runContainerLoad(name, entryUrl));
          } catch (err) {
            reject(err);
          }
        })
        .catch(() => undefined);
    },
  );
};

export const loadNextjsRemote = async (
  remote: RemoteConfig,
  remoteOrigin: string,
): Promise<ComponentType> => {
  let container: WebpackContainer;
  let shim: RuntimeShim;
  try {
    ({ container, shim } = await loadRemoteContainer(
      remote.name,
      remote.entry,
    ));

    try {
      await container.init({});
    } catch {
      // Container was already initialized on a previous route visit — safe to ignore.
    }

    await registerNavigationShim(container, shim, remoteOrigin);

    const factory = await container.get(`./${remote.exposedModule}`);
    const moduleExports = factory();

    const Inner = extractComponent(
      moduleExports,
      remote.name,
      remote.exposedModule,
    );

    const Wrapper: ComponentType<Record<string, unknown>> = props => {
      const theme = useTheme();

      useEffect(() => {
        watchImageSources(remoteOrigin);
        return () => {
          unwatchImageSources(remoteOrigin);
        };
      }, []);

      return createElement(Inner, { ...props, theme } as ComponentProps<
        typeof Inner
      >);
    };
    Wrapper.displayName = `MFRemote(${remote.name})`;

    return Wrapper;
  } catch (err) {
    remoteCache.delete(remote.name);
    throw err;
  }
};
